import { afterNextRender, DestroyRef, inject, Injectable } from '@angular/core';

import { LanguageService } from '@23degrees/shared/services/language';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import merge from 'lodash/merge';
import set from 'lodash/set';
import * as CookieConsent from 'vanilla-cookieconsent';
import { GoogleService } from '../google/google.service';
import { CONSENT_CONFIG, CONSENT_CONFIG_DEFAULT, CONSENT_ENABLED, CONSENT_SECTION_TRANSLATION_FIELDS, CONSENT_TRANSLATION_FIELDS } from './config';
import {
  CAT_ADVERTISEMENT,
  CAT_ANALYTICS,
  CAT_FUNCTIONALITY,
  CAT_SECURITY,
  SERVICE_AD_PERSONALIZATION,
  SERVICE_AD_STORAGE,
  SERVICE_AD_USER_DATA,
  SERVICE_ANALYTICS_STORAGE,
  SERVICE_FUNCTIONALITY_STORAGE,
  SERVICE_PERSONALIZATION_STORAGE,
  SERVICE_SECURITY_STORAGE,
} from './constants';

declare const gtag: any;

@Injectable({
  providedIn: 'root',
})
export class ConsentBannerService {
  public googleService = inject(GoogleService);
  public enabled = inject(CONSENT_ENABLED);
  public config = inject(CONSENT_CONFIG);
  public fullConfig = merge(CONSENT_CONFIG_DEFAULT, this.config);
  public languageService = inject(LanguageService);
  public destroyRef = inject(DestroyRef);
  private currentLang = this.languageService.currentLang;

  async translate(lang: string) {
    const translations = this.fullConfig.language.translations[lang] as CookieConsent.Translation;
    for await (const prop of CONSENT_TRANSLATION_FIELDS) {
      set(translations, prop.field, await this.languageService.translateStringAsync(prop.key));
    }

    const categories = this.fullConfig.categories;
    for await (const prop of CONSENT_SECTION_TRANSLATION_FIELDS) {
      set(categories, prop.field, await this.languageService.translateStringAsync(prop.key));
    }
  }

  init() {
    this.enabled = true;
    afterNextRender(() => {
      this.languageService.currentLang$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (lang) => {
        await this.translate(lang);
        this.fullConfig.language.default = lang;

        if (this.currentLang !== lang) {
          CookieConsent.reset();
          this.run();
          this.show();
        } else {
          this.run();
        }
        
        this.googleService.useFactory();
        this.currentLang = lang;
      });
    });
  }

  run() {
    if (!this.enabled) return;

    CookieConsent.run({
      onFirstConsent: () => {
        // console.log('ON FIRST');
        // this.updateGtagConsent();
      },
      onConsent: () => {
        // console.log('ON CONSENT');
        this.updateGtagConsent();
      },
      onChange: () => {
        // console.log('ON CHANGE');
        this.updateGtagConsent();
      },
      autoShow: false,
      ...this.fullConfig,
    });
  }

  show(forceOpen: boolean = false) {
    if (!this.enabled) return;
    if (!CookieConsent.validConsent() || forceOpen) {
      CookieConsent.show(true);
    }
  }

  getUserPreferences() {
    if (!this.enabled) return;
    return CookieConsent.getUserPreferences();
  }

  showPreferences() {
    if (!this.enabled) return;
    CookieConsent.showPreferences();
  }

  updateGtagConsent() {
    if (!this.enabled) return;
    gtag('consent', 'update', {
      [SERVICE_ANALYTICS_STORAGE]: CookieConsent.acceptedService(SERVICE_ANALYTICS_STORAGE, CAT_ANALYTICS) ? 'granted' : 'denied',
      [SERVICE_AD_STORAGE]: CookieConsent.acceptedService(SERVICE_AD_STORAGE, CAT_ADVERTISEMENT) ? 'granted' : 'denied',
      [SERVICE_AD_USER_DATA]: CookieConsent.acceptedService(SERVICE_AD_USER_DATA, CAT_ADVERTISEMENT) ? 'granted' : 'denied',
      [SERVICE_AD_PERSONALIZATION]: CookieConsent.acceptedService(SERVICE_AD_PERSONALIZATION, CAT_ADVERTISEMENT) ? 'granted' : 'denied',
      [SERVICE_FUNCTIONALITY_STORAGE]: CookieConsent.acceptedService(SERVICE_FUNCTIONALITY_STORAGE, CAT_FUNCTIONALITY) ? 'granted' : 'granted',
      [SERVICE_PERSONALIZATION_STORAGE]: CookieConsent.acceptedService(SERVICE_PERSONALIZATION_STORAGE, CAT_FUNCTIONALITY) ? 'granted' : 'granted',
      [SERVICE_SECURITY_STORAGE]: CookieConsent.acceptedService(SERVICE_SECURITY_STORAGE, CAT_SECURITY) ? 'granted' : 'granted',
    });
    // gtag('event', 'consent_update');
  }
}
